import type { CSSProperties, FC } from 'react';
import type React from 'react';

import { CustomButton } from 'components/header/style';
import { Button, TextInput, Tooltip, Typography } from 'enable-ui';

import {
  AddContent,
  ButtonAdd,
  ButtonAddContainer,
  ButtonEdit,
  DropdownContent,
  Options,
} from '../styles/dashboard';

import { ReactComponent as AddCircleIcon } from 'assets/icons/add-icon.svg';
import { ReactComponent as AddIcon } from 'assets/icons/plus_blue.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';

import { useBoolean } from 'hooks';
import { isEmpty } from 'lodash';

import { COLOR } from 'constants/color';

export const AddItemModal: FC<{
  title: string;
  placeholderSearch?: string;
  searchValue?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  options: any[];
  onAdd: () => void;
  content?: React.ReactNode;
  buttonStyle?: React.CSSProperties;
  showIcon?: boolean;
  createButton?: React.ReactNode;
  onClickOutside?: () => void;
  formMode?: '' | 'add' | 'edit';
  selectedData?: any;
  isShowAddFormButton?: boolean;
  extraDropdownContentStyle?: CSSProperties;
}> = ({
  title,
  placeholderSearch,
  searchValue,
  onChange,
  options,
  onAdd,
  content,
  buttonStyle,
  showIcon = true,
  createButton,
  onClickOutside = null,
  formMode = '',
  selectedData,
  extraDropdownContentStyle,
  isShowAddFormButton = false,
}) => {
  const visible = useBoolean(false);
  const isFormAddMode = formMode === 'add' && isEmpty(selectedData);

  return (
    <div style={{ position: `${isFormAddMode ? 'static' : 'relative'}` }}>
      <Tooltip
        bg="transparent"
        trigger="click"
        arrow={false}
        placement="bottom"
        appendTo={document.body}
        content={
          <DropdownContent style={{ minWidth: 440, maxHeight: 576, ...extraDropdownContentStyle }}>
            <TextInput
              name="name"
              placeholder={placeholderSearch}
              prefix={<SearchIcon />}
              value={searchValue ?? ''}
              onChange={onChange}
            />
            {options.length > 0 ? (
              <Options>{content}</Options>
            ) : (
              <Typography style={{ textAlign: 'center' }}>No data</Typography>
            )}
            <div
              style={{
                padding: 12,
                borderTop: `1px solid ${COLOR.neutralTrans['neutralTrans-25']}`,
              }}
            >
              <CustomButton>
                <Button
                  style={{ width: '100%' }}
                  onClick={() => {
                    onAdd();
                    visible.setValue(false);
                  }}
                >
                  Add
                </Button>
              </CustomButton>
            </div>

            {createButton && (
              <div
                onClick={() => {
                  visible.setValue(false);
                }}
              >
                {createButton}
              </div>
            )}
          </DropdownContent>
        }
        visible={visible.value}
        offset={[5, 25]}
        allowHTML
        interactive
        onClickOutside={() => {
          visible.setValue(false);
          onClickOutside?.();
        }}
      >
        {isShowAddFormButton ? (
          isFormAddMode ? (
            <ButtonAddContainer>
              <ButtonAdd onClick={() => visible.setValue((prev) => !prev)}>
                <AddIcon /> {title}
              </ButtonAdd>
            </ButtonAddContainer>
          ) : (
            <div style={{ position: 'absolute', left: 0, bottom: 6 }}>
              <ButtonEdit onClick={() => visible.setValue((prev) => !prev)}>
                <AddIcon /> {title}
              </ButtonEdit>
            </div>
          )
        ) : (
          <AddContent
            onClick={() => visible.setValue((prev) => !prev)}
            style={{ borderLeft: 'none', padding: 0, ...buttonStyle }}
          >
            {showIcon && <AddIcon />}
            <Typography style={{ marginLeft: 8, whiteSpace: 'nowrap' }}>{title}</Typography>
          </AddContent>
        )}
      </Tooltip>
    </div>
  );
};
